import { useEffect, useState } from 'react'

import ChatBubble from './chat-bubble'
import LoadingBubble from './loading-bubble'
import { CurrentMessageFIFO, updateCurrentMessageFIFO, constructSubQuestions } from './utils/streaming'
import {
  PacketType,
  SubQuestionDetail,
  BackendMessage,
  ToolCallMetadata,
  OnyxDocument,
  RetrievalType,
  FileDescriptor,
  StreamStopReason,
  StreamingError,
  MessageResponseIDInfo,
  RefinedAnswerImprovement,
  StreamStopInfo,
  SubQuestionPiece,
  SubQueryPiece,
  AgentAnswerPiece,
  AnswerPiecePacket,
  DocumentsResponse,
  DocumentInfoPacket,
  SEARCH_TOOL_NAME,
  FileChatDisplay,
  ChatFileType,
} from './types'

export type StreamingResponse = {
  packet: PacketType
  answer: string
  second_level_answer: string
  sub_questions: SubQuestionDetail[]
  is_generating: boolean
  second_level_generating: boolean
  finalMessage: BackendMessage | null
  toolCall: ToolCallMetadata | null
  isImprovement: boolean | undefined
  isStreamingQuestions: boolean
  documents: OnyxDocument[]
  retrievalType: RetrievalType
  error: string | null
  stackTrace: string | null
  aiMessageImages: FileDescriptor[] | null
  agenticDocs: OnyxDocument[] | null
}

type StreamingMessageProps = {
  message: string
  chatSessionId: string
  parentMessageId?: number
  onComplete?: (data: StreamingResponse) => void
  onError?: (error: string) => void
  scrollToBottom?: () => void
  onViewSources?: (data: StreamingResponse) => void
  isSelected?: boolean
}

export default function StreamingMessage({
  message,
  chatSessionId,
  parentMessageId,
  onComplete,
  onError,
  scrollToBottom,
  onViewSources,
  isSelected,
}: StreamingMessageProps) {
  const [state, setState] = useState<'loading' | 'streaming' | 'complete' | 'error'>('loading')
  const [data, setData] = useState<StreamingResponse | null>(null)

  useEffect(() => {
    const signal = new AbortController()
    let latestData: StreamingResponse | null = null
    async function stream() {
      let initialFetchDetails: null | {
        user_message_id: number
        assistant_message_id: number
      } = null

      let answer = ''
      let second_level_answer = ''

      const stopReason: StreamStopReason | null = null
      let query: string | null = null
      let aiMessageImages: FileDescriptor[] | null = null
      let agenticDocs: OnyxDocument[] | null = null
      let error: string | null = null
      let stackTrace: string | null = null

      let sub_questions: SubQuestionDetail[] = []
      let is_generating: boolean = false
      let second_level_generating: boolean = false
      let finalMessage: BackendMessage | null = null
      let toolCall: ToolCallMetadata | null = null
      let isImprovement: boolean | undefined = undefined
      let isStreamingQuestions = true
      let documents: OnyxDocument[] = []
      let retrievalType: RetrievalType = RetrievalType.None

      const stack = new CurrentMessageFIFO()
      updateCurrentMessageFIFO(stack, {
        message,
        sessionId: chatSessionId,
        signal: signal.signal,
        parentMessageId,
      })

      const delay = (ms: number) => {
        return new Promise((resolve) => setTimeout(resolve, ms))
      }

      await delay(50)
      while (!stack.isComplete || !stack.isEmpty()) {
        if (stack.isEmpty()) {
          await delay(0.5)
        }

        if (!stack.isEmpty()) {
          const packet = stack.nextPacket()
          if (!packet) {
            continue
          }
          if (!initialFetchDetails) {
            if (!Object.hasOwn(packet, 'user_message_id')) {
              console.error('First packet should contain message response info ')
              if (Object.hasOwn(packet, 'error')) {
                const error = (packet as StreamingError).error
                onError?.(error)
                return
              }
              continue
            }

            const messageResponseIDInfo = packet as MessageResponseIDInfo

            const user_message_id = messageResponseIDInfo.user_message_id!
            const assistant_message_id = messageResponseIDInfo.reserved_assistant_message_id

            initialFetchDetails = {
              assistant_message_id,
              user_message_id,
            }
          } else {
            setState('streaming')

            if (Object.hasOwn(packet, 'level')) {
              if ((packet as any).level === 1) {
                second_level_generating = true
              }
            }

            if (Object.hasOwn(packet, 'refined_answer_improvement')) {
              isImprovement = (packet as RefinedAnswerImprovement).refined_answer_improvement
            }

            if (Object.hasOwn(packet, 'stream_type')) {
              if ((packet as any).stream_type == 'main_answer') {
                is_generating = false
                second_level_generating = true
              }
            }

            if (Object.hasOwn(packet, 'message_id')) {
              finalMessage = packet as BackendMessage
            }

            // // Continuously refine the sub_questions based on the packets that we receive
            if (Object.hasOwn(packet, 'stop_reason') && Object.hasOwn(packet, 'level_question_num')) {
              if ((packet as StreamStopInfo).stream_type == 'main_answer') {
                setState('streaming')
              }
              if (
                (packet as StreamStopInfo).stream_type == 'sub_questions' &&
                (packet as StreamStopInfo).level_question_num == undefined
              ) {
                isStreamingQuestions = false
              }
              sub_questions = constructSubQuestions(sub_questions, packet as StreamStopInfo)
            } else if (Object.hasOwn(packet, 'sub_question')) {
              // setState('toolBuilding')
              is_generating = true
              sub_questions = constructSubQuestions(sub_questions, packet as SubQuestionPiece)
            } else if (Object.hasOwn(packet, 'sub_query')) {
              sub_questions = constructSubQuestions(sub_questions, packet as SubQueryPiece)
            } else if (
              Object.hasOwn(packet, 'answer_piece') &&
              Object.hasOwn(packet, 'answer_type') &&
              (packet as AgentAnswerPiece).answer_type === 'agent_sub_answer'
            ) {
              sub_questions = constructSubQuestions(sub_questions, packet as AgentAnswerPiece)
            } else if (Object.hasOwn(packet, 'answer_piece')) {
              // Mark every sub_question's is_generating as false
              sub_questions = sub_questions.map((subQ) => ({
                ...subQ,
                is_generating: false,
              }))

              if (Object.hasOwn(packet, 'level') && (packet as any).level === 1) {
                second_level_answer += (packet as AnswerPiecePacket).answer_piece
              } else {
                answer += (packet as AnswerPiecePacket).answer_piece
              }
            } else if (
              Object.hasOwn(packet, 'top_documents') &&
              Object.hasOwn(packet, 'level_question_num') &&
              (packet as DocumentsResponse).level_question_num != undefined
            ) {
              const documentsResponse = packet as DocumentsResponse
              sub_questions = constructSubQuestions(sub_questions, documentsResponse)

              if (documentsResponse.level_question_num === 0 && documentsResponse.level == 0) {
                documents = (packet as DocumentsResponse).top_documents
              } else if (documentsResponse.level_question_num === 0 && documentsResponse.level == 1) {
                agenticDocs = (packet as DocumentsResponse).top_documents
              }
            } else if (Object.hasOwn(packet, 'top_documents')) {
              documents = (packet as DocumentInfoPacket).top_documents
              retrievalType = RetrievalType.Search

              // if (documents && documents.length > 0) {
              //   // point to the latest message (we don't know the messageId yet, which is why
              //   // we have to use -1)
              //   setSelectedMessageForDocDisplay(user_message_id)
              // }
            } else if (Object.hasOwn(packet, 'tool_name')) {
              // Will only ever be one tool call per message
              toolCall = {
                tool_name: (packet as ToolCallMetadata).tool_name,
                tool_args: (packet as ToolCallMetadata).tool_args,
                tool_result: (packet as ToolCallMetadata).tool_result,
              }

              if (!toolCall.tool_name.includes('agent')) {
                // if (!toolCall.tool_result || toolCall.tool_result == undefined) {
                //   // setState('toolBuilding')
                // } else {
                setState('streaming')
                // }

                // This will be consolidated in upcoming tool calls udpate,
                // but for now, we need to set query as early as possible
                if (toolCall.tool_name == SEARCH_TOOL_NAME) {
                  query = toolCall.tool_args['query']
                }
              } else {
                toolCall = null
              }
            } else if (Object.hasOwn(packet, 'file_ids')) {
              aiMessageImages = (packet as FileChatDisplay).file_ids.map((fileId) => {
                return {
                  id: fileId,
                  type: ChatFileType.IMAGE,
                }
              })
            } else if (Object.hasOwn(packet, 'error') && (packet as StreamingError).error !== null) {
              if (
                sub_questions.length > 0 &&
                sub_questions.filter((q) => q.level === 0).every((q) => q.is_stopped === true)
              ) {
                onError?.((packet as StreamingError).error)
                setState('error')
                return
                // throw new Error((packet as StreamingError).error);
              } else {
                error = (packet as StreamingError).error
                stackTrace = (packet as StreamingError).stack_trace
                onError?.(error)
                setState('error')
                return
              }
            } else if (Object.hasOwn(packet, 'stop_reason')) {
              // const stop_reason = (packet as StreamStopInfo).stop_reason
              // if (stop_reason === StreamStopReason.CONTEXT_LENGTH) {
              //   updateCanContinue(true)
              // }
            }

            latestData = {
              packet,
              answer,
              second_level_answer,
              sub_questions,
              is_generating,
              second_level_generating,
              finalMessage,
              toolCall,
              isImprovement,
              isStreamingQuestions,
              documents,
              retrievalType,
              error,
              stackTrace,
              aiMessageImages,
              agenticDocs,
            }
            setData(latestData)
            scrollToBottom?.()
          }
        }
      }
      latestData && onComplete?.(latestData)
      setState('complete')
    }
    stream()

    return () => {
      signal.abort()
    }
  }, [message, chatSessionId])

  if (state === 'loading' || !data?.answer.trim()) return <LoadingBubble />

  return (
    <ChatBubble
      from="ai"
      state={state}
      isSelected={isSelected}
      message={data.answer}
      documents={data.documents}
      onViewSources={() => {
        onViewSources?.(data)
      }}
    />
  )
}
